/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Styles for the assignment due date modal.
 *
 * @file assignmentDueDateModalStyles.js
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import { Dialog } from '@mui/material';
import { styled } from '@mui/material/styles';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';
import { createScopedClasses, createScopedStyles } from '../../../assets/styles/createScopedStyles';

const contentScrollStyles = CommonStyles().scrollContainer({
  scrollbarWidth: 8,
  scrollbarColor: `${mixins.quaternary} transparent`,
  overflowY: 'auto'
});

const assignmentDueDateModalStyles = {
  paper: {
    borderRadius: 24,
    padding: 48,
    width: 546,
    maxWidth: 546,
    boxSizing: 'border-box',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(13, 0, 77, 0.04), 0 1px 0.5px rgba(13, 0, 77, 0.1), 0 2px 0.5px rgba(13, 0, 77, 0.15)'
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    padding: 4,
    color: mixins.color_text_primary
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: 0,
    minHeight: 0,
    flex: 1,
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',

    '& .react-datepicker': {
      boxSizing: 'border-box'
    }
  },
  title: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 24,
    fontWeight: mixins.fontWeight500,
    lineHeight: '32px',
    letterSpacing: '-0.48px',
    color: mixins.color_text_primary,
    margin: 0
  },
  scrollBody: {
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    ...contentScrollStyles,
    overflowX: 'hidden'
  },
  body: {
    width: '100%',
    maxWidth: 450,
    minWidth: 0
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    maxWidth: 450,
    minWidth: 0
  },
  doneButton: {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
    padding: '16px 24px',
    borderRadius: 1000,
    border: 'none',
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: mixins.amethystColorV2
    },
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      outlineOffset: 2
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  }
};

export const classes = createScopedClasses('aet-assignment-due-date-modal', assignmentDueDateModalStyles);

export const scopedStyles = createScopedStyles(assignmentDueDateModalStyles, classes);

/**
 * The dialog surface renders in a portal, so the scope root is the dialog
 * itself rather than a wrapper element inside the component tree.
 */
export const AssignmentDueDateModalStylesRoot = styled(Dialog)(scopedStyles);

export default assignmentDueDateModalStyles;
